import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { GraduationCap, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import PasswordInput from "@/components/PasswordInput";
import { useNoIndex } from "@/lib/useSeo";

export default function ResetPassword() {
  useNoIndex();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [serverError, setServerError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      toast.error("Les mots de passe ne correspondent pas");
      return;
    }
    setLoading(true);
    setServerError("");
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Mot de passe réinitialisé. Connecte-toi maintenant.");
      navigate("/login");
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || "Erreur";
      setServerError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2" data-testid="reset-password-page">
      <div className="flex flex-col justify-center px-8 md:px-16 py-12 bg-white">
        <div className="max-w-sm w-full mx-auto">
          <Link to="/login" className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-8">
            <ArrowLeft size={14} /> Retour à la connexion
          </Link>
          <div className="flex items-center gap-2 mb-8">
            <div className="w-10 h-10 rounded-xl bg-indigo-500 text-white flex items-center justify-center">
              <GraduationCap size={22} />
            </div>
            <span className="font-display text-xl font-bold text-slate-900">Student Hub</span>
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 mb-2">Nouveau mot de passe</h1>
          <p className="text-slate-500 mb-8">Choisis un mot de passe d'au moins 6 caractères.</p>
          {!token ? (
            <div className="rounded-xl border border-red-200 bg-red-50 p-5" data-testid="reset-no-token">
              <p className="text-red-700 font-semibold">Lien invalide ou incomplet</p>
              <p className="text-red-600 text-sm mt-1">
                Le lien que tu as ouvert ne contient pas de jeton de réinitialisation. Demande un nouveau lien depuis la page mot de passe oublié.
              </p>
              <Link to="/forgot-password" className="inline-block mt-3 px-4 py-2 rounded-full bg-red-600 text-white text-sm font-medium hover:bg-red-700">
                Demander un nouveau lien
              </Link>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="space-y-5" data-testid="reset-form">
              {serverError && (
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-4" data-testid="reset-server-error">
                  <p className="text-amber-800 text-sm font-medium">{serverError}</p>
                  <Link to="/forgot-password" className="text-amber-900 text-xs underline mt-1 inline-block">
                    Demander un nouveau lien
                  </Link>
                </div>
              )}
              <div>
                <Label htmlFor="p">Nouveau mot de passe</Label>
                <PasswordInput id="p" required minLength={6} value={password}
                       onChange={(e) => setPassword(e.target.value)} className="mt-1.5" data-testid="reset-password-input" />
              </div>
              <div>
                <Label htmlFor="c">Confirmer</Label>
                <PasswordInput id="c" required minLength={6} value={confirm}
                       onChange={(e) => setConfirm(e.target.value)} className="mt-1.5" data-testid="reset-confirm-input" />
              </div>
              <Button type="submit" disabled={loading} className="w-full bg-indigo-500 hover:bg-indigo-600 h-11" data-testid="reset-submit-btn">
                {loading ? "Modification..." : "Mettre à jour"}
              </Button>
            </form>
          )}
        </div>
      </div>
      <div className="hidden md:block bg-cover bg-center relative"
           style={{ backgroundImage: "url('https://images.unsplash.com/photo-1620831468075-db24ca183258?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600')" }}>
        <div className="absolute inset-0 bg-gradient-to-br from-yellow-200/30 via-indigo-200/20 to-indigo-600/30" />
      </div>
    </div>
  );
}
